/** TanStack Query hooks for search and autocomplete suggestions */

import { useQuery } from '@tanstack/react-query'
import { apiClient } from '../lib/api-client'
import type { SearchResponse, SuggestResponse } from '@agentwiki/shared'

type SearchSource = 'docs' | 'storage' | 'all'

/**
 * Hybrid search hook — triggers for queries >= 2 chars. `project` (the project the user is working in) only scopes
 * the analytics record, so a search that finds nothing shows up under that project's Health; results stay
 * workspace-wide.
 */
export function useSearch(query: string, source: SearchSource = 'all', project?: string) {
  return useQuery<SearchResponse>({
    queryKey: ['search', query, source, project],
    queryFn: () =>
      apiClient.get<SearchResponse>(
        `/api/search?q=${encodeURIComponent(query)}&type=hybrid&limit=10&source=${source}${project ? `&project=${encodeURIComponent(project)}` : ''}`,
      ),
    enabled: query.length >= 2,
    staleTime: 30_000,
    placeholderData: (prev) => prev, // keep previous results while fetching new query
  })
}

/** Autocomplete suggestions hook — triggers for queries >= 1 char */
export function useSuggest(query: string) {
  return useQuery<SuggestResponse>({
    queryKey: ['suggest', query],
    queryFn: () =>
      apiClient.get<SuggestResponse>(
        `/api/search/suggest?q=${encodeURIComponent(query)}&limit=7`,
      ),
    enabled: query.length >= 1,
    staleTime: 60_000,
    placeholderData: (prev) => prev, // keep previous suggestions while fetching
  })
}
